import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import Joyride, {
  ACTIONS,
  type CallBackProps,
  EVENTS,
  STATUS,
} from 'react-joyride';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { useFertWorkSessionPolling } from '@/app/hooks/useFertWorkSessionPolling';
import {
  useCreateFertWorkSessionMutation,
  useDeleteSessionMutation,
  useGetFertWorkSessionsQuery,
} from '@/app/services/fertWork';
import '@/components/guided-tour/tour.css';
import { TourHelpButton } from '@/components/guided-tour/TourHelpButton';
import { TourTooltip } from '@/components/guided-tour/TourTooltip';
import useLocalStorage from '@/components/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getDateString } from '@/pages/fert-work/utils/dateHelpers';
import { filterNewSessionSites } from '@/pages/fert-work/utils/sitePickerFilters';
import {
  STATUS_CODES,
  statusCodeToLabel,
} from '@/pages/fert-work/utils/statusHelpers';
import { hasPermission, MODULES } from '@/services/auth';
import { useSites } from '@/services/sites';
import { useToast } from '@/services/toast';

import { SessionHistoryGrid } from './components/SessionHistoryGrid';
import { SessionsGrid } from './components/SessionsGrid';
import { sessionsListSteps } from './tours/sessionsListTour';

import './fertWorkTypography.css';

export type FertWorkPageProps = {
  className?: string;
};

type SessionRow = {
  id: string;
  sessionId: string;
  date: string;
  site: string;
  status: number;
  siteId: string;
};

const FertWorkPageContent = ({ className = '' }: FertWorkPageProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useAppSelector((state) => state.authentication);
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab = tabParam === 'history' ? 1 : 0;

  const setActiveTab = (index: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (index === 1) {
          next.set('tab', 'history');
        } else {
          next.delete('tab');
        }
        return next;
      },
      { replace: true },
    );
  };

  const [tourCompleted, setTourCompleted] = useLocalStorage(
    'tour-completed-fert-work-sessions-list',
    false,
  );
  const [tourRun, setTourRun] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  useEffect(() => {
    if (!tourCompleted && sessionsListSteps.length > 0 && !tourRun) {
      const timer = setTimeout(() => setTourRun(true), 500);
      return () => clearTimeout(timer);
    }
  }, [tourCompleted]);

  useEffect(() => {
    const clearScroll = () => {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
      const main = document.querySelector('main');
      if (main && main.style.overflow) {
        main.style.removeProperty('overflow');
      }
    };
    if (!tourRun) {
      clearScroll();
      const timer = setTimeout(clearScroll, 100);
      return () => clearTimeout(timer);
    }
    return clearScroll;
  }, [tourRun]);

  const startTour = useCallback(() => {
    setActiveTab(0);
    setTourStepIndex(0);
    setTimeout(() => setTourRun(true), 300);
  }, [setActiveTab]);

  const handleTourCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, type, status } = data;

      if (
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        action === ACTIONS.CLOSE
      ) {
        setTourRun(false);
        setTourStepIndex(0);
        setTourCompleted(true);
        setActiveTab(0);
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextIndex =
          action === ACTIONS.NEXT
            ? index + 1
            : action === ACTIONS.PREV
              ? index - 1
              : index;
        if (nextIndex < 0 || nextIndex >= sessionsListSteps.length) {
          setTourRun(false);
          setTourStepIndex(0);
          setTourCompleted(true);
          setActiveTab(0);
          return;
        }
        const nextTab = (sessionsListSteps[nextIndex].data as any)?.tab ?? 0;
        if (nextTab !== activeTabRef.current) {
          setTourRun(false);
          setActiveTab(nextTab);
          setTimeout(() => {
            setTourStepIndex(nextIndex);
            setTourRun(true);
          }, 400);
        } else {
          setTourStepIndex(nextIndex);
        }
        return;
      }

      if (type === EVENTS.TARGET_NOT_FOUND) {
        if (index + 1 < sessionsListSteps.length) {
          const nextTab = (sessionsListSteps[index + 1].data as any)?.tab ?? 0;
          if (nextTab !== activeTabRef.current) {
            setTourRun(false);
            setActiveTab(nextTab);
            setTimeout(() => {
              setTourStepIndex(index + 1);
              setTourRun(true);
            }, 400);
          } else {
            setTourStepIndex(index + 1);
          }
        } else {
          setTourRun(false);
          setTourStepIndex(0);
          setTourCompleted(true);
        }
      }
    },
    [setActiveTab, setTourCompleted],
  );
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [existingSession, setExistingSession] = useState<SessionRow | null>(
    null,
  );
  const [newlyCreatedSession, setNewlyCreatedSession] = useState<{
    id: string;
    date: string;
    siteId: string;
  } | null>(null);
  const [aspirationDate, setAspirationDate] = useState<Date | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedSessionType, setSelectedSessionType] =
    useState<string>('conv_sf');

  const sessionTypeOptions = [
    {
      label: t('fertWork.conventionalSf', 'Conventional / SF'),
      value: 'conv_sf',
    },
    { label: t('fertWork.commercial', 'Commercial'), value: 'commercial' },
  ];

  const { data: fertilizationSites } = useSites({ filter: 'fertilization' });
  const [shouldPollSessions, setShouldPollSessions] = useState(false);

  const [activeFilters, setActiveFilters] = useState<{
    siteId?: string | null;
    date?: string | null;
    status?: string | null;
    createdById?: string | null;
  }>({});

  const [historyFilters, setHistoryFilters] = useState<{
    siteId?: string | null;
    date?: string | null;
    status?: string | null;
    createdById?: string | null;
  }>({});

  // Server pagination is currently broken (the API returns no total count, so
  // AG Grid client-side pagination over a server-paginated slice can never
  // advance past page 1). Fetch a generous batch and let AG Grid paginate the
  // result client-side; filters still refetch server-side with the filter
  // applied. SESSION_FETCH_LIMIT is well above any realistic filtered set.
  const SESSION_FETCH_LIMIT = 1000;

  const activeQueryParams = useMemo(() => {
    const params: Record<string, any> = {
      startRow: 1,
      endRow: SESSION_FETCH_LIMIT,
    };
    if (activeFilters.siteId) params.siteId = activeFilters.siteId;
    if (activeFilters.date) params.aspirationDate = activeFilters.date;
    if (activeFilters.status) params.status = activeFilters.status;
    if (activeFilters.createdById)
      params.createdById = activeFilters.createdById;
    return params;
  }, [activeFilters]);

  const {
    data: apiSessions = [],
    isLoading: isLoadingSessions,
    refetch,
  } = useGetFertWorkSessionsQuery(activeQueryParams, {
    pollingInterval: shouldPollSessions ? 2000 : 0,
  });

  const historyQueryParams = useMemo(() => {
    const params: Record<string, any> = {
      startRow: 1,
      endRow: SESSION_FETCH_LIMIT,
    };
    if (historyFilters.siteId) params.siteId = historyFilters.siteId;
    if (historyFilters.date) params.aspirationDate = historyFilters.date;
    if (historyFilters.status) params.status = historyFilters.status;
    if (historyFilters.createdById)
      params.createdById = historyFilters.createdById;
    return params;
  }, [historyFilters]);

  const { data: historySessions = [], isLoading: isLoadingHistory } =
    useGetFertWorkSessionsQuery(historyQueryParams, { skip: activeTab !== 1 });
  const [createSession, { isLoading: isCreating }] =
    useCreateFertWorkSessionMutation();
  const [deleteSession, { isLoading: isDeleting }] = useDeleteSessionMutation();

  const recentSessions = useMemo(() => {
    const latestBySite = new Map<string, (typeof apiSessions)[number]>();
    for (const session of apiSessions) {
      const existing = latestBySite.get(session.siteId);
      if (
        !existing ||
        new Date(session.createdAt).getTime() >
          new Date(existing.createdAt).getTime()
      ) {
        latestBySite.set(session.siteId, session);
      }
    }
    return Array.from(latestBySite.values());
  }, [apiSessions]);

  useEffect(() => {
    setShouldPollSessions(
      apiSessions.some((s) => s.status === STATUS_CODES.VALIDATING),
    );
  }, [apiSessions]);

  const { status: processingStatus, isPolling } = useFertWorkSessionPolling(
    newlyCreatedSession?.id || null,
  );

  const hasWritePermission = hasPermission(me, MODULES.FERT_WORK_WRITE);

  useEffect(() => {
    if (!isPolling && newlyCreatedSession && !showProcessingModal) {
      setNewlyCreatedSession(null);
    }
  }, [isPolling, newlyCreatedSession, showProcessingModal]);

  useEffect(() => {
    if (processingStatus && newlyCreatedSession) {
      refetch();
    }
  }, [processingStatus?.status, newlyCreatedSession, refetch]);

  const handleNewSession = () => {
    setShowNewSessionDialog(true);
  };

  const handleStartSession = async () => {
    if (!aspirationDate || !selectedSiteId) return;

    const year = aspirationDate.getFullYear();
    const month = String(aspirationDate.getMonth() + 1).padStart(2, '0');
    const day = String(aspirationDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    const duplicate = apiSessions.find(
      (session) =>
        getDateString(session.aspirationDate) === formattedDate &&
        session.siteId === selectedSiteId,
    );

    if (duplicate) {
      setExistingSession({
        id: duplicate.id,
        sessionId: duplicate.sessionId,
        date: formattedDate,
        site: duplicate.siteName,
        siteId: duplicate.siteId,
        status: duplicate.status,
      });
      setShowDuplicateWarning(true);
      return;
    }

    await createNewSession(formattedDate);
  };

  const createNewSession = async (formattedDate: string) => {
    if (!selectedSiteId) return;

    try {
      const result = await createSession({
        aspirationDate: formattedDate,
        siteId: selectedSiteId,
        sessionType: selectedSessionType === 'commercial' ? 1 : 0,
      }).unwrap();

      setShowNewSessionDialog(false);
      setShowDuplicateWarning(false);

      setNewlyCreatedSession({
        id: result.id,
        date: formattedDate,
        siteId: selectedSiteId,
      });
      setShowProcessingModal(true);
    } catch (error) {
      console.error('Failed to create session:', error);
      toast.show({
        severity: 'error',
        summary: t(
          'fertWork.toasts.sessionCreationFailed',
          'Session Creation Failed',
        ),
        detail: t(
          'fertWork.toasts.sessionCreationFailedDetail',
          'Failed to create session. Please try again.',
        ),
        life: 5000,
      });
    }
  };

  const handleOpenExistingSession = () => {
    if (!existingSession) return;

    setShowNewSessionDialog(false);
    setShowDuplicateWarning(false);
    setAspirationDate(null);
    setSelectedSiteId(null);
    setSelectedSessionType('conv_sf');

    navigate(
      `/fert-work/session?sessionId=${existingSession.id}&date=${existingSession.date}&siteId=${existingSession.siteId}`,
    );
  };

  const handleCreateAnyway = async () => {
    if (!aspirationDate) return;
    const year = aspirationDate.getFullYear();
    const month = String(aspirationDate.getMonth() + 1).padStart(2, '0');
    const day = String(aspirationDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    await createNewSession(formattedDate);
  };

  const handleCancelNewSession = () => {
    setShowNewSessionDialog(false);
    setShowDuplicateWarning(false);
    setAspirationDate(null);
    setSelectedSiteId(null);
    setSelectedSessionType('conv_sf');
    setExistingSession(null);
  };

  const handleDeleteSession = (sessionId: string) => {
    setSessionToDelete(sessionId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;

    try {
      await deleteSession(sessionToDelete).unwrap();
      setShowDeleteConfirm(false);
      setSessionToDelete(null);
      toast.show({
        severity: 'success',
        summary: t('fertWork.toasts.sessionDeleted', 'Session Deleted'),
        detail: t(
          'fertWork.toasts.sessionDeletedDetail',
          'Session has been successfully deleted.',
        ),
        life: 3000,
      });
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.deleteFailed', 'Delete Failed'),
        detail: t(
          'fertWork.toasts.deleteFailedDetail',
          'Failed to delete session. Please try again.',
        ),
        life: 5000,
      });
    }
  };

  const handleDeleteMultiple = async (sessionIds: string[]) => {
    let successCount = 0;
    let failCount = 0;

    for (const sessionId of sessionIds) {
      try {
        await deleteSession(sessionId).unwrap();
        successCount++;
      } catch (error) {
        console.error(`Failed to delete session ${sessionId}:`, error);
        failCount++;
      }
    }

    if (failCount === 0) {
      toast.show({
        severity: 'success',
        summary: t('fertWork.toasts.sessionsDeleted', 'Sessions Deleted'),
        detail: t(
          'fertWork.toasts.sessionsDeletedDetail',
          'Successfully deleted {count} session(s).',
          {
            count: successCount,
          },
        ),
        life: 3000,
      });
    } else {
      toast.show({
        severity: 'warn',
        summary: t('fertWork.toasts.partialDelete', 'Partial Delete'),
        detail: t(
          'fertWork.toasts.partialDeleteDetail',
          'Deleted {successCount} session(s), {failCount} failed.',
          {
            successCount,
            failCount,
          },
        ),
        life: 5000,
      });
    }
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setShowDeleteConfirm(false);
    setSessionToDelete(null);
  };

  const handleRowDoubleClick = (
    sessionId: string,
    date: string,
    siteId: string,
  ) => {
    navigate(
      `/fert-work/session?sessionId=${sessionId}&date=${date}&siteId=${siteId}`,
    );
  };

  const handleGoToSession = () => {
    if (!newlyCreatedSession) return;

    navigate(
      `/fert-work/session?sessionId=${newlyCreatedSession.id}&date=${newlyCreatedSession.date}&siteId=${newlyCreatedSession.siteId}`,
    );
  };

  const handleStayOnPage = () => {
    setShowProcessingModal(false);
    setAspirationDate(null);
    setSelectedSiteId(null);
    setSelectedSessionType('conv_sf');
    setExistingSession(null);
  };

  return (
    <div
      className={`fert-work-page relative flex flex-auto flex-col ${className}`}
    >
      {tourRun && (
        <Joyride
          steps={sessionsListSteps}
          run={tourRun}
          stepIndex={tourStepIndex}
          callback={handleTourCallback}
          continuous
          showSkipButton
          spotlightClicks
          disableScrolling
          disableScrollParentFix
          tooltipComponent={TourTooltip}
          styles={{ options: { zIndex: 10000 } }}
        />
      )}
      <div className="flex flex-col gap-2 p-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <h2 className="text-base font-medium whitespace-nowrap">
              {t('fertWork.fertWorkSessions', 'Fert Work Sessions')}
            </h2>
            <TourHelpButton onClick={startTour} />
          </div>
          {hasWritePermission && (
            <div data-tour="new-session-btn">
              <Button size="sm" onClick={handleNewSession}>
                <i className="pi pi-plus" data-icon="inline-start" />
                {t('fertWork.newSession', 'New Session')}
              </Button>
            </div>
          )}
        </div>

        <div>
          <div
            data-tour="session-tabs"
            className="flex border-b border-gray-200"
          >
            <button
              type="button"
              className={`flex-1 border-0 bg-transparent px-4 py-2 text-sm font-medium transition-colors md:flex-none ${
                activeTab === 0
                  ? 'border-b-[3px] border-b-cyan-600 text-cyan-700'
                  : 'border-b-[3px] border-b-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab(0)}
            >
              {t('fertWork.recentSessions', 'Recent Sessions')}
            </button>
            <button
              type="button"
              className={`flex-1 border-0 bg-transparent px-4 py-2 text-sm font-medium transition-colors md:flex-none ${
                activeTab === 1
                  ? 'border-b-[3px] border-b-cyan-600 text-cyan-700'
                  : 'border-b-[3px] border-b-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab(1)}
            >
              {t('fertWork.sessionHistory', 'Session History')}
            </button>
          </div>

          <div data-tour="sessions-grid" className="pt-4">
            {activeTab === 0 && (
              <SessionsGrid
                sessions={recentSessions}
                isLoading={isLoadingSessions}
                onRowDoubleClick={handleRowDoubleClick}
                onFilterChange={setActiveFilters}
              />
            )}

            {activeTab === 1 && (
              <SessionHistoryGrid
                sessions={historySessions}
                isLoading={isLoadingHistory}
                hasWritePermission={hasWritePermission}
                onDelete={handleDeleteSession}
                onDeleteMultiple={handleDeleteMultiple}
                onRowDoubleClick={handleRowDoubleClick}
                onFilterChange={setHistoryFilters}
              />
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={showNewSessionDialog}
        onOpenChange={(open) => {
          if (!open) handleCancelNewSession();
        }}
      >
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('fertWork.startNewSession', 'Start New Session')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="aspiration-date">
                {t('fertWork.aspirationDate', 'Aspiration Date')}
              </label>
              <DatePicker
                id="aspiration-date"
                value={aspirationDate ?? undefined}
                onChange={(date) => setAspirationDate(date ?? null)}
                placeholder={t('fertWork.selectDate', 'Select date')}
                className="h-8 w-full"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="fert-site">
                {t('fertWork.fertSite', 'Fert Site')}
              </label>
              <Select
                items={filterNewSessionSites(fertilizationSites).map(
                  (site) => ({ label: site.name, value: site.id }),
                )}
                value={selectedSiteId}
                onChange={setSelectedSiteId}
              >
                <SelectTrigger id="fert-site" className="w-full">
                  <SelectValue
                    placeholder={t(
                      'fertWork.selectFertSite',
                      'Select fert site',
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {filterNewSessionSites(fertilizationSites).map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="session-type">
                {t('fertWork.sessionType', 'Session Type')}
              </label>
              <Select
                items={sessionTypeOptions}
                value={selectedSessionType}
                onChange={(value) => setSelectedSessionType(value as string)}
              >
                <SelectTrigger id="session-type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sessionTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={handleCancelNewSession}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleStartSession}
              disabled={!aspirationDate || !selectedSiteId || isCreating}
            >
              {isCreating && <i className="pi pi-spin pi-spinner" />}
              {t('fertWork.start', 'Start')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDuplicateWarning}
        onOpenChange={(open) => {
          if (!open) setShowDuplicateWarning(false);
        }}
      >
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('fertWork.sessionAlreadyExists', 'Session Already Exists')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <i className="pi pi-exclamation-triangle text-xl text-yellow-600" />
              <span>
                A session already exists for{' '}
                <strong>
                  {
                    fertilizationSites?.find((s) => s.id === selectedSiteId)
                      ?.name
                  }
                </strong>{' '}
                on <strong>{aspirationDate?.toLocaleDateString()}</strong>.
              </span>
            </div>
            <div className="rounded bg-gray-50 p-3">
              <div className="mb-1 text-sm text-gray-600">
                {t('fertWork.existingSession', 'Existing Session:')}:
              </div>
              <div className="font-medium">
                {t('fertWork.status', 'Status')}:{' '}
                <span
                  className={
                    existingSession?.status === 0
                      ? 'text-purple-600'
                      : existingSession?.status === 1
                        ? 'text-blue-600'
                        : existingSession?.status === 2
                          ? 'text-green-600'
                          : existingSession?.status === 3
                            ? 'text-red-600'
                            : existingSession?.status === 4
                              ? 'text-yellow-600'
                              : 'text-gray-600'
                  }
                >
                  {existingSession
                    ? statusCodeToLabel(existingSession.status)
                    : ''}
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              {t(
                'fertWork.openOrCreateNew',
                'You can open the existing session or create a new one anyway.',
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setShowDuplicateWarning(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleOpenExistingSession}>
              {t('fertWork.openExisting', 'Open Existing')}
            </Button>
            <Button
              variant="warning"
              onClick={handleCreateAnyway}
              disabled={isCreating}
            >
              {isCreating && <i className="pi pi-spin pi-spinner" />}
              {t('fertWork.createNew', 'Create New')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          if (!open) handleCancelDelete();
        }}
      >
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('fertWork.deleteSession', 'Delete Session')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <i className="pi pi-exclamation-triangle text-xl text-red-600" />
              <span>
                {t(
                  'fertWork.deleteSessionConfirm',
                  'Are you sure you want to delete this session? This action cannot be undone.',
                )}
              </span>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting && <i className="pi pi-spin pi-spinner" />}
              {t('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showProcessingModal} onOpenChange={() => {}}>
        <DialogContent showCloseButton={false} className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('fertWork.processingSession', 'Processing Session')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <div className="flex items-start gap-2">
              <i className="pi pi-info-circle text-xl text-blue-600" />
              <div className="flex-1">
                <div className="mb-1 font-medium">
                  {t(
                    'fertWork.sessionDataBeingRetrieved',
                    'Your session data is being retrieved and validated',
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {t(
                    'fertWork.processMayTakeMoments',
                    'This process may take a few moments depending on the amount of data.',
                  )}
                </div>
              </div>
            </div>

            {processingStatus && (
              <div className="rounded-md bg-blue-50 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900">
                    {processingStatus.status === STATUS_CODES.VALIDATING
                      ? t('fertWork.validating', 'Validating...')
                      : processingStatus.status === STATUS_CODES.IN_PROGRESS
                        ? t(
                            'fertWork.readyForGeneration',
                            'Ready for Generation',
                          )
                        : processingStatus.status === STATUS_CODES.ISSUES
                          ? t(
                              'fertWork.validationCompleteIssuesFound',
                              'Validation Complete - Issues Found',
                            )
                          : processingStatus.status === STATUS_CODES.COMPLETE
                            ? t('fertWork.complete', 'Complete')
                            : processingStatus.status === STATUS_CODES.ERROR
                              ? t('fertWork.error', 'Error')
                              : t('fertWork.processing', 'Processing...')}
                  </span>
                </div>

                {processingStatus.status === STATUS_CODES.VALIDATING && (
                  <div className="mb-2 h-2.5 w-full overflow-hidden rounded-full bg-blue-200">
                    <div className="h-2.5 w-full animate-pulse rounded-full bg-blue-600"></div>
                  </div>
                )}

                {processingStatus.status === STATUS_CODES.ISSUES && (
                  <div className="text-sm text-yellow-800">
                    {t(
                      'fertWork.foundValidationIssues',
                      'Found {count} validation issue(s) that need attention',
                      {
                        count: processingStatus.issueCount,
                      },
                    )}
                  </div>
                )}

                {processingStatus.status === STATUS_CODES.ERROR && (
                  <div className="text-sm text-red-800">
                    {processingStatus.errorMessage ||
                      t(
                        'fertWork.errorDuringProcessing',
                        'An error occurred during processing',
                      )}
                  </div>
                )}
              </div>
            )}

            <div className="text-sm text-gray-600">
              {t(
                'fertWork.navigateOrStay',
                'You can navigate to the session page to view the details, or stay here to create another session.',
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={handleStayOnPage}>
              {t('fertWork.stayHere', 'Stay Here')}
            </Button>
            <Button onClick={handleGoToSession}>
              {t('fertWork.goToSession', 'Go to Session')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const FertWorkPage = (props: FertWorkPageProps) => (
  <ConfirmProvider>
    <FertWorkPageContent {...props} />
  </ConfirmProvider>
);
